import * as React from 'react'
import { addYears, format } from 'date-fns'
import { DayPicker, getDefaultClassNames, useDayPicker } from 'react-day-picker'
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { buttonVariants } from '@/components/ui/button'
import { Icon } from '@/common/components/Icon'

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: 'primary' | 'secondary' | 'tertiary'
  captionLayout?: 'label' | 'dropdown'
}

function CalendarMonthCaption({
  calendarMonth,
  className,
  displayIndex,
  ...props
}: {
  calendarMonth: { date: Date }
  className?: string
  displayIndex?: number
} & React.HTMLAttributes<HTMLDivElement>) {
  const { goToMonth, nextMonth, previousMonth } = useDayPicker()
  const monthDate = calendarMonth.date
  const previousYear = addYears(monthDate, -1)
  const nextYear = addYears(monthDate, 1)

  return (
    <div
      className={cn(
        'flex items-center !justify-between gap-2 w-full border-b border-monochrome-120 pb-5 mb-5 mt-[8px]',
        className,
      )}
      {...props}
    >
      <div className="flex items-center gap-2">
        <button
          type="button"
          className={cn(
            buttonVariants({ variant: 'ghost', size: 'icon-sm' }),
            'h-8 w-8 bg-transparent text-white/70 hover:text-white',
          )}
          onClick={() => {
            if (previousMonth) {
              goToMonth(previousYear)
            }
          }}
          disabled={!previousMonth}
          aria-label="Go to previous year"
        >
          <Icon
            icon="double-chevron-left"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            className="rotate-180"
            fill="none"
          />
        </button>
        <button
          type="button"
          className={cn(
            buttonVariants({ variant: 'ghost', size: 'icon-sm' }),
            'h-8 w-8 bg-transparent text-white/70 hover:text-white',
          )}
          onClick={() => {
            if (previousMonth) {
              goToMonth(previousMonth)
            }
          }}
          disabled={!previousMonth}
          aria-label="Go to previous month"
        >
          <Icon
            icon="chevron-left"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          />
        </button>
      </div>
      <span className="text-sm font-semibold text-white">
        {format(monthDate, 'MMM yyyy')}
      </span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          className={cn(
            buttonVariants({ variant: 'ghost', size: 'icon-sm' }),
            'h-8 w-8 bg-transparent text-white/70 hover:text-white',
          )}
          onClick={() => {
            if (nextMonth) {
              goToMonth(nextMonth)
            }
          }}
          disabled={!nextMonth}
          aria-label="Go to next month"
        >
          <Icon
            icon="chevron-left"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            className="rotate-180"
          />
        </button>
        <button
          type="button"
          className={cn(
            buttonVariants({ variant: 'ghost', size: 'icon-sm' }),
            'h-8 w-8 bg-transparent text-white/70 hover:text-white',
          )}
          onClick={() => {
            if (nextMonth) {
              goToMonth(nextYear)
            }
          }}
          disabled={!nextMonth}
          aria-label="Go to next year"
        >
          <Icon
            icon="double-chevron-left"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          />
        </button>
      </div>
    </div>
  )
}

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  buttonVariant = 'primary',
  captionLayout = 'label',
  ...props
}: CalendarProps) {
  const defaultClassNames = getDefaultClassNames()

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('p-3 h-full flex flex-col', className)}
      classNames={{
        root: cn('w-full h-full flex flex-col', defaultClassNames.root),
        months: cn(
          'flex gap-4 flex-col md:flex-row relative flex-1',
          defaultClassNames.months,
        ),
        month: cn('flex flex-col w-full flex-1', defaultClassNames.month),
        nav: cn(
          'flex items-center gap-1 w-full absolute top-0 inset-x-0 justify-between',
          defaultClassNames.nav,
        ),
        button_previous: cn(
          buttonVariants({ variant: buttonVariant }),
          'size-(--cell-size) aria-disabled:opacity-50 p-0 select-none',
          defaultClassNames.button_previous,
        ),
        button_next: cn(
          buttonVariants({ variant: buttonVariant }),
          'size-(--cell-size) aria-disabled:opacity-50 p-0 select-none',
          defaultClassNames.button_next,
        ),
        month_caption: cn(
          'flex items-center justify-center h-(--cell-size) w-full px-(--cell-size)',
          defaultClassNames.month_caption,
        ),
        dropdowns: cn(
          'w-full flex items-center text-sm font-medium justify-center h-(--cell-size) gap-1.5',
          defaultClassNames.dropdowns,
        ),
        dropdown_root: cn(
          'relative has-focus:border-ring border border-input shadow-xs has-focus:ring-ring/50 has-focus:ring-[3px] rounded-md',
          defaultClassNames.dropdown_root,
        ),
        dropdown: cn(
          'absolute bg-popover inset-0 opacity-0',
          defaultClassNames.dropdown,
        ),
        caption_label: cn(
          'select-none font-medium',
          captionLayout === 'label'
            ? 'text-sm'
            : 'rounded-md pl-2 pr-1 flex items-center gap-1 text-sm h-8 [&>svg]:text-muted-foreground [&>svg]:size-3.5',
          defaultClassNames.caption_label,
        ),
        month_grid: cn(
          'w-full border-collapse !flex flex-col flex-1',
          defaultClassNames.month_grid,
        ),
        weekdays: cn('flex w-full', defaultClassNames.weekdays),
        weekday: cn(
          'text-body-medium text-monochrome-1 rounded-md flex-1 font-normal text-[0.8rem] select-none',
          defaultClassNames.weekday,
        ),
        weeks: cn('!flex flex-col flex-1', defaultClassNames.weeks),
        week: cn('flex w-full flex-1 items-center', defaultClassNames.week),
        week_number_header: cn(
          'select-none w-(--cell-size)',
          defaultClassNames.week_number_header,
        ),
        week_number: cn(
          'text-[0.8rem] select-none text-muted-foreground',
          defaultClassNames.week_number,
        ),
        day: cn(
          'relative flex-1 p-0 text-body-medium text-monochrome-1 text-center group/day select-none data-[selected=true]:bg-accent-1 data-[selected=true]:text-white data-[selected=true]:hover:bg-accent-1 rounded-full flex items-center justify-center',
          defaultClassNames.day,
        ),
        day_button: cn(
          buttonVariants({ variant: 'ghost', size: 'sm' }),
          'rounded-[10px] py-1 px-2 w-full h-full text-white/90 hover:bg-white/10',
          'data-[selected=true]:bg-accent-1 data-[selected=true]:text-white data-[selected=true]:hover:bg-accent-1',
        ),
        range_start: cn(
          'rounded-l-md bg-accent',
          defaultClassNames.range_start,
        ),
        range_middle: cn('rounded-none', defaultClassNames.range_middle),
        range_end: cn('rounded-r-md bg-accent', defaultClassNames.range_end),
        today: cn(
          'border border-monochrome-120 text-white rounded-full',
          defaultClassNames.today,
        ),
        outside: cn(
          'text-muted-foreground aria-selected:text-muted-foreground',
          defaultClassNames.outside,
        ),
        disabled: cn(
          'text-muted-foreground opacity-50',
          defaultClassNames.disabled,
        ),
        hidden: cn('invisible', defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Nav: () => <div className="hidden" />,
        MonthCaption: CalendarMonthCaption,
        Chevron: ({ orientation, ...iconProps }) =>
          orientation === 'right' ? (
            <Icon
              icon="chevron-down"
              width="8"
              height="16"
              viewBox="0 0 8 16"
              {...iconProps}
            />
          ) : (
            <Icon
              icon="chevron-down"
              width="8"
              height="16"
              viewBox="0 0 8 16"
              {...iconProps}
            />
          ),
      }}
      {...props}
    />
  )
}
Calendar.displayName = 'Calendar'

export { Calendar }
